/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Waveform Timeline & Interactive Scrubber (Task 10.5.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useRef, useEffect, useState, useMemo } from 'react';
import { ZoomIn, ZoomOut, AudioWaveform } from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useSubtitleStudioStore,
  formatTimecode,
} from '../../stores/useSubtitleStudioStore';

export const WaveformTimelineScrubber: React.FC = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const {
    audioFile,
    cues,
    currentTime,
    zoomLevel,
    activeCueId,
    selectedCueId,
    seek,
    selectCue,
    setZoomLevel,
  } = useSubtitleStudioStore();

  const [isDragging, setIsDragging] = useState(false);

  const totalDuration = Math.max(10, audioFile?.durationSec || 36.5);
  const pixelsPerSecond = 24 * zoomLevel;
  const timelineWidth = Math.max(800, totalDuration * pixelsPerSecond);

  // Generate deterministic synthetic waveform peak data
  const waveformPeaks = useMemo(() => {
    const barsCount = Math.max(100, Math.floor(timelineWidth / 4));
    const peaks: number[] = [];
    for (let i = 0; i < barsCount; i++) {
      // Deterministic pseudo-random variation based on sine & cosine
      const t = i / barsCount;
      const base = Math.sin(t * 30) * 0.3 + Math.cos(t * 80) * 0.2 + 0.45;
      const variation = Math.abs(Math.sin(i * 12.9898) * 0.25);
      peaks.push(Math.min(0.95, Math.max(0.12, base + variation)));
    }
    return peaks;
  }, [timelineWidth]);

  // Draw waveform on HTML5 canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = timelineWidth;
    const height = 70;
    canvas.width = width;
    canvas.height = height;

    ctx.clearRect(0, 0, width, height);

    const barWidth = 2.5;
    const barGap = 1.5;
    const totalBars = waveformPeaks.length;
    const currentProgressX = (currentTime / totalDuration) * width;

    for (let i = 0; i < totalBars; i++) {
      const x = i * (barWidth + barGap);
      const peak = waveformPeaks[i];
      const barHeight = Math.max(4, peak * (height - 12));
      const y = (height - barHeight) / 2;

      // Color past vs future waveform
      if (x <= currentProgressX) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.9)'; // Primary blue active
      } else {
        ctx.fillStyle = 'rgba(156, 163, 175, 0.35)'; // Subdued grey inactive
      }

      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, 1.5);
      ctx.fill();
    }
  }, [waveformPeaks, currentTime, totalDuration, timelineWidth]);

  // Handle scrubber click & drag
  const handleTimelineInteraction = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const scrollLeft = containerRef.current.scrollLeft;
    const relativeX = clientX - rect.left + scrollLeft;
    const clampedX = Math.max(0, Math.min(timelineWidth, relativeX));
    const targetTime = (clampedX / timelineWidth) * totalDuration;
    seek(targetTime);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    handleTimelineInteraction(e.clientX);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        handleTimelineInteraction(e.clientX);
      }
    };
    const handleMouseUp = () => {
      if (isDragging) setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Time ruler ticks (every 5 seconds)
  const ticksCount = Math.ceil(totalDuration / 5);
  const timeTicks = Array.from({ length: ticksCount + 1 }, (_, i) => i * 5).filter(
    (s) => s <= totalDuration
  );

  const playheadPositionPx = (currentTime / totalDuration) * timelineWidth;

  return (
    <div className="bg-surface rounded-2xl border border-border p-4 shadow-sm space-y-3">
      {/* Top Header Controls: Title & Zoom */}
      <div className="flex items-center justify-between pb-2 border-b border-border text-xs">
        <div className="flex items-center gap-2 text-text font-semibold">
          <AudioWaveform className="size-4 text-primary" />
          <span>{audioFile?.name || 'Audio Track'}</span>
          <span className="text-text-muted font-normal">
            ({formatTimecode(currentTime)} / {formatTimecode(totalDuration)})
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="text-text-muted font-medium">{t('subtitles.zoomLabel')}:</span>
          <button
            type="button"
            disabled={zoomLevel <= 1}
            onClick={() => setZoomLevel(Math.max(1, zoomLevel - 1))}
            className="p-1 rounded hover:bg-surface-subtle disabled:opacity-40 text-text transition-colors"
          >
            <ZoomOut className="size-3.5" />
          </button>
          <span className="px-2 py-0.5 rounded bg-surface-subtle border border-border font-mono text-[11px] text-text">
            {zoomLevel}x
          </span>
          <button
            type="button"
            disabled={zoomLevel >= 4}
            onClick={() => setZoomLevel(Math.min(4, zoomLevel + 1))}
            className="p-1 rounded hover:bg-surface-subtle disabled:opacity-40 text-text transition-colors"
          >
            <ZoomIn className="size-3.5" />
          </button>
        </div>
      </div>

      {/* Scrollable Timeline Viewport */}
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        className="relative overflow-x-auto select-none rounded-xl border border-border bg-surface-subtle cursor-crosshair h-[160px]"
      >
        <div
          style={{ width: `${timelineWidth}px` }}
          className="relative h-full flex flex-col justify-between py-1"
        >
          {/* 1. Timecode Ruler */}
          <div className="h-6 border-b border-border/60 relative">
            {timeTicks.map((sec) => {
              const tickLeft = (sec / totalDuration) * timelineWidth;
              return (
                <div
                  key={sec}
                  style={{ left: `${tickLeft}px` }}
                  className="absolute top-0 flex flex-col items-start"
                >
                  <span className="text-[10px] font-mono text-text-muted select-none pl-1">
                    {formatTimecode(sec)}
                  </span>
                  <div className="w-[1px] h-2 bg-border" />
                </div>
              );
            })}
          </div>

          {/* 2. Waveform Canvas */}
          <div className="relative h-[70px] flex items-center">
            <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />
          </div>

          {/* 3. Subtitle Cue Blocks Lane */}
          <div className="h-[46px] border-t border-border/60 relative px-1 flex items-center">
            {cues.map((cue) => {
              const cueLeft = (cue.start / totalDuration) * timelineWidth;
              const cueWidth = Math.max(16, ((cue.end - cue.start) / totalDuration) * timelineWidth);
              const isActive = cue.id === activeCueId;
              const isSelected = cue.id === selectedCueId;

              return (
                <div
                  key={cue.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectCue(cue.id);
                    seek(cue.start);
                  }}
                  style={{
                    left: `${cueLeft}px`,
                    width: `${cueWidth}px`,
                  }}
                  className={`absolute h-8 rounded-lg border px-2 flex items-center justify-between text-[11px] font-medium truncate cursor-pointer transition-all shadow-xs ${
                    isActive
                      ? 'bg-primary text-primary-foreground border-primary ring-2 ring-primary/30 z-20'
                      : isSelected
                      ? 'bg-primary/20 text-primary border-primary z-10'
                      : 'bg-surface text-text border-border hover:border-primary/50'
                  }`}
                  title={`#${cue.index} (${formatTimecode(cue.start)} - ${formatTimecode(cue.end)}): ${cue.text}`}
                >
                  <span className="font-mono text-[10px] opacity-80 mr-1.5 shrink-0">
                    #{cue.index}
                  </span>
                  <span className="truncate">{cue.text || '...'}</span>
                </div>
              );
            })}
          </div>

          {/* 4. Interactive Scrubber Playhead Line */}
          <div
            style={{ left: `${playheadPositionPx}px` }}
            className="absolute top-0 bottom-0 w-[2px] bg-rose-500 pointer-events-none z-30 transition-transform"
          >
            {/* Playhead Time Badge */}
            <div className="absolute -top-1 -left-7 px-1.5 py-0.5 rounded bg-rose-500 text-white font-mono text-[9px] font-bold shadow-md">
              {formatTimecode(currentTime)}
            </div>
            <div className="w-2.5 h-2.5 bg-rose-500 rounded-full -ml-[4px] shadow-sm" />
          </div>
        </div>
      </div>
    </div>
  );
};
